'use client'

import React, { createContext, useContext, useState, useCallback } from 'react'
import * as Toast from '@radix-ui/react-toast'
import type { ToasterProps } from './Toaster'
import Toaster from './Toaster'

type ToastIntent = ToasterProps['intent']

export type ToastData = {
    intent: ToastIntent
    title: string
    description?: string
    action?: { label: string; onClick: () => void }
    persistent?: boolean
    isClosable?: boolean
}

type ToastContextType = {
    showToast: (data: ToastData) => void
}

const ToastContext = createContext<ToastContextType | null>(null)

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
    const [toast, setToast] = useState<ToastData | null>(null)
    const [open, setOpen] = useState(false)

    const showToast = useCallback(
        (data: ToastData) => {
            if (open) {
                setOpen(false)
                setTimeout(() => {
                    setToast(data)
                    setOpen(true)
                }, 100)
            } else {
                setToast(data)
                setOpen(true)
            }
        },
        [open]
    )

    const handleOpenChange = useCallback((isOpen: boolean) => {
        setOpen(isOpen)
        if (!isOpen) {
            setTimeout(() => {
                setToast(null)
            }, 300)
        }
    }, [])

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <Toast.Provider>
                {toast && (
                    <Toaster
                        intent={toast.intent}
                        title={toast.title}
                        description={toast?.description}
                        action={toast.action}
                        persistent={toast.persistent}
                        isClosable={toast.isClosable}
                        open={open}
                        onOpenChange={handleOpenChange}
                    />
                )}
            </Toast.Provider>
        </ToastContext.Provider>
    )
}

export const useToast = () => {
    const context = useContext(ToastContext)
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider')
    }
    return context
}
